import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages - New Structure
import NewLandingPage from "@/pages/NewLandingPage";
import UnifiedDashboard from "@/pages/UnifiedDashboard";
import TalentHome from "@/pages/TalentHome";
import AuthCallback from "@/pages/AuthCallback";
import QuickSignup from "@/pages/QuickSignup";
// Old pages for redirect compatibility
import InfluencerHome from "@/pages/influencer/Home";
import InfluencerDashboard from "@/pages/influencer/Dashboard";
import InfluencerProfile from "@/pages/influencer/Profile";
import BrandHome from "@/pages/brand/Home";
import BrandDiscover from "@/pages/brand/Discover";
import BrandHub from "@/pages/brand/Hub";
import BrandProfile from "@/pages/brand/Profile";
import HireTalent from "@/pages/HireTalent";
import AboutPage from "@/pages/About";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
// Legal Pages
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import TermsConditions from "@/pages/legal/TermsConditions";
import AccountDelete from "@/pages/legal/AccountDelete";
// Public profile
import StarPublicProfile from "@/pages/StarPublicProfile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults for better mobile compatibility
axios.defaults.timeout = 30000;

// Helper to get auth token from storage (localStorage or sessionStorage fallback)
const getStoredToken = () => {
  try {
    return localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  } catch (e) {
    console.error("Storage access error:", e);
    return null;
  }
};

// Helper to save auth token to storage
export const saveAuthToken = (token) => {
  try {
    localStorage.setItem("authToken", token);
    // Also save to sessionStorage as backup for mobile
    sessionStorage.setItem("authToken", token);
  } catch (e) {
    console.error("Storage save error:", e);
    // Try sessionStorage only
    try {
      sessionStorage.setItem("authToken", token);
    } catch (e2) {
      console.error("SessionStorage also failed:", e2);
    }
  }
};

// Helper to clear auth token
export const clearAuthToken = () => {
  try {
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("authToken");
  } catch (e) {
    console.error("Storage clear error:", e);
  }
};

// Add request interceptor to always include auth token
axios.interceptors.request.use(
  (config) => {
    // Don't add auth header to login/register endpoints
    if (config.url?.includes('/auth/login') || config.url?.includes('/auth/register')) {
      return config;
    }
    
    const token = getStoredToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle auth errors
// Public routes that should NOT redirect on 401
const PUBLIC_ROUTES = ['/', '/hire-talent', '/privacy-policy', '/refund-policy', '/terms-conditions', '/account-delete', '/about', '/star', '/quick-signup'];

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      const currentPath = window.location.pathname;
      const isPublicRoute = PUBLIC_ROUTES.some(route => currentPath === route || currentPath.startsWith(route + '/'));
      if (!currentPath.includes('/admin') && !isPublicRoute) {
        clearAuthToken();
        // Only redirect if not on a public route
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // First try with stored token (using helper that checks both storages)
      const storedToken = getStoredToken();
      const headers = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
      
      const response = await axios.get(`${API}/auth/me`, {
        headers,
        timeout: 5000
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      clearAuthToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Failsafe: If loading takes more than 8 seconds, force it to false
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Auth check timeout - forcing load complete');
        setLoading(false);
      }
    }, 8000);
    
    checkAuth();
    
    return () => clearTimeout(timeout);
  }, [checkAuth]);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      saveAuthToken(token);
    }
  };

  const logout = async () => {
    try {
      const storedToken = getStoredToken();
      const headers = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    clearAuthToken();
  };

  // Get auth headers helper
  const getAuthHeaders = () => {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Special handling for admin routes - check localStorage
  if (allowedTypes?.includes("admin")) {
    const adminUser = localStorage.getItem("adminUser");
    if (adminUser) {
      try {
        const admin = JSON.parse(adminUser);
        if (admin.user_type === "admin") {
          return children;
        }
      } catch (e) {
        // Invalid admin data
      }
    }
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    // Redirect to correct dashboard
    if (user.user_type === "influencer") {
      return <Navigate to="/influencer/home" replace />;
    } else {
      return <Navigate to="/brand/home" replace />;
    }
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* New Landing Page */}
      <Route path="/" element={<NewLandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Unified Dashboard - New */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedTypes={["influencer"]}>
            <UnifiedDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Old Influencer Routes - Now using TalentHome */}
      <Route
        path="/influencer/home"
        element={
          <ProtectedRoute allowedTypes={["influencer"]}>
            <TalentHome />
          </ProtectedRoute>
        }
      />
      <Route path="/influencer/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/influencer/profile"
        element={
          <ProtectedRoute allowedTypes={["influencer"]}>
            <InfluencerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/influencer/about"
        element={
          <ProtectedRoute allowedTypes={["influencer"]}>
            <AboutPage />
          </ProtectedRoute>
        }
      />

      {/* Brand Routes */}
      <Route
        path="/brand/home"
        element={
          <ProtectedRoute allowedTypes={["brand"]}>
            <BrandHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand/discover"
        element={
          <ProtectedRoute allowedTypes={["brand"]}>
            <BrandDiscover />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand/hub"
        element={
          <ProtectedRoute allowedTypes={["brand"]}>
            <BrandHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand/profile"
        element={
          <ProtectedRoute allowedTypes={["brand"]}>
            <BrandProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand/about"
        element={
          <ProtectedRoute allowedTypes={["brand"]}>
            <AboutPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Hire Talent - Public */}
      <Route path="/hire-talent" element={<HireTalent />} />

      {/* Quick Signup - Public (Admin generated links) */}
      <Route path="/quick-signup" element={<QuickSignup />} />

      {/* Legal Pages - Public */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/account-delete" element={<AccountDelete />} />

      {/* Public Star Profile (shareable) */}
      <Route path="/star/:userId" element={<StarPublicProfile />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
